import { RoleName } from '@core/types/RoleName';

export type AppVersion = 'v2' | 'v3';

// This is the v2 (legacy, CRA) app. The new OHIF v3 app lives in a separate
// repository/deployment (evodicom-web) and mirrors this same constants file
// with these two literals swapped.
export const CURRENT_APP_VERSION: AppVersion = 'v2';
export const OTHER_APP_VERSION: AppVersion = 'v3';

export const APP_VERSION_URLS: Record<AppVersion, string> = {
  v2: process.env.REACT_APP_V2_URL ?? '',
  v3: process.env.REACT_APP_V3_URL ?? '',
};

// True only once the deployment has actually been given a usable URL for
// that version. Covers three states of the pipeline getting this wrong: the
// env var missing entirely (empty string), and — since .env only ever holds
// a #{TOKEN}# placeholder for the prod pipeline's replacetokens step — the
// token still being unresolved because nobody added the matching secret yet
// (actionOnMissing: 'warn' lets the build continue with the literal
// "#{...}#" string baked in instead of failing it).
export const hasAppUrl = (version: AppVersion): boolean => {
  const url = APP_VERSION_URLS[version];
  return Boolean(url) && !url.startsWith('#{');
};

// hasAppUrl only proves the value is present and resolved — it can't catch
// a URL that's configured but wrong, e.g. both versions' URLs pointing at
// the same origin (a copy-paste mistake, or a local .env.local left over
// from a different port layout). Redirecting to your own origin doesn't
// error — it just reloads the same route, which re-runs the same redirect
// on mount and loops forever. Guard every actual redirect with this instead
// of hasAppUrl.
export const canRedirectTo = (version: AppVersion): boolean => {
  if (!hasAppUrl(version)) {
    return false;
  }
  try {
    return new URL(APP_VERSION_URLS[version]).origin !== window.location.origin;
  } catch {
    // Malformed URL in config — treat as unusable rather than throwing.
    return false;
  }
};

// Organization-wide viewer version, set by admins from Personalización and
// stored in organization settings. Applies to every user (and to anonymous
// shared-study links) except Radiologists who have pinned their own
// preference. Missing or unrecognized values fall back to v2.
export const ORGANIZATION_VIEWER_VERSION_KEY = 'ViewerVersion';
export const DEFAULT_ORGANIZATION_APP_VERSION: AppVersion = 'v2';

const isAppVersion = (value: unknown): value is AppVersion => value === 'v2' || value === 'v3';

export const getOrganizationAppVersion = (
  organizationSettings?: Record<string, unknown>,
): AppVersion => {
  const value = organizationSettings?.[ORGANIZATION_VIEWER_VERSION_KEY];
  return isAppVersion(value) ? value : DEFAULT_ORGANIZATION_APP_VERSION;
};

interface ResolveAppVersionParams {
  role?: string;
  userPreference?: AppVersion;
  organizationVersion: AppVersion;
}

// A Radiologist's own choice (header switch / Preferences) wins; everyone
// else — and Radiologists who never chose — follows the organization setting.
export const resolveAppVersion = ({
  role,
  userPreference,
  organizationVersion,
}: ResolveAppVersionParams): AppVersion =>
  role === RoleName.Radiologist && isAppVersion(userPreference)
    ? userPreference
    : organizationVersion;
